import { useEffect, useMemo, useState } from "react";
import { Button, Card, Stack, Typography } from "@material-ui/core";
import useCalendar from "hooks/useCalendar";
import { useTranslation } from "react-i18next";
import { getFilteredEventsByType } from "utils/calendar/filters";
import { CalendarTasksLoading } from "./CalendarTasksLoading";
import { CalendarTasksNotFound } from "./CalendarTasksNotFound";
import { CalendarTasksTabs } from "./CalendarTasksTabs";
import { CalendarTasksRow } from "./CalendarTasksRow";
import { AddTask } from "@material-ui/icons";
import { CalendarTasksToolbar } from "./CalendarTasksToolbar";
import { adjustTaskTimeForToday } from "utils/calendar/helper";
import { CalendarEventDialog } from "components/__haim/Common/CalendarEventDialog";
import { useModal } from "hooks/useModal";
import { sortAssignedTasks } from "utils/calendar/officeTasks";
import { useHasOfficeColleagues } from "./CalendarTaskAssigneeSelect";
import type { MouseEvent, ReactNode } from "react";
import type { EventTaskTab } from "./CalendarTasksTabs";
import type { CalendarEvent } from "../../../@types/sharedSchema";

/**
 * The events of ONE listing — the user's own plus their teammates' org-shared
 * ones (see `useListingEvents`). Passing it switches this component from "my
 * day" mode to the listing-scoped simple list.
 */
type ListingEventsData = {
  events: CalendarEvent[];
  isLoading: boolean;
};

type CalendarTasksProps = {
  listingEvents?: ListingEventsData;
  inListingMatch?: boolean;
  customEventProps?: Partial<CalendarEvent>;
  onAddNewTask?: () => void;
};

export function CalendarTasks({
  listingEvents,
  inListingMatch,
  customEventProps,
  onAddNewTask
}: CalendarTasksProps): JSX.Element {
  const [currentTab, setCurrentTab] = useState<EventTaskTab>("today");

  const { t: translate } = useTranslation();

  const { open, openModal, closeModal } = useModal();

  const {
    events,
    assignedTasks,
    isFetchingEvents,
    deleteEvent,
    initiateLoadEvents,
    createOrUpdateEvent,
    updateEventTimeById,
    getInitialEventByType
  } = useCalendar();

  const parsedEvents = listingEvents ? listingEvents.events : events;
  const parsedLoading = listingEvents ? listingEvents.isLoading : isFetchingEvents;

  useEffect(() => {
    if (listingEvents) return;

    initiateLoadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCurrentTabChange = (_event: MouseEvent, newValue: EventTaskTab) => {
    setCurrentTab(newValue);
  };

  const useSimpleUi = Boolean(listingEvents);

  const overdueEvents = getFilteredEventsByType(parsedEvents, "overdue");

  const handleMoveAllOverdueTasksToToday = async (): Promise<void> => {
    // Deliberately not a Firestore batch: going through the context notifies the
    // server per event, which is what mirrors the new times into Google
    // Calendar. A batch write would stay invisible to Google until the next
    // hourly reconcile pass.
    try {
      await Promise.all(
        overdueEvents.map(({ id, start: startDate, end: endDate, allDay }) => {
          const { start, end } = adjustTaskTimeForToday(startDate, endDate);
          return updateEventTimeById(id, start, end, Boolean(allDay));
        })
      );
    } catch (error) {
      console.error("Error updating events", error);
    }
  };

  const parsedCurrentTab = useSimpleUi ? null : currentTab;

  // "הקצתי לאחרים": tasks the user put in colleagues' calendars. Shown to anyone with a
  // colleague to assign to, and to anyone who still has assigned tasks after leaving an office.
  const hasOfficeColleagues = useHasOfficeColleagues();
  const showAssignedTab = !useSimpleUi && (hasOfficeColleagues || (assignedTasks?.length ?? 0) > 0);
  const isAssignedTab = parsedCurrentTab === "assignedByMe";

  const filteredEvents = useMemo(
    () =>
      isAssignedTab
        ? sortAssignedTasks(assignedTasks ?? [])
        : getFilteredEventsByType(parsedEvents, parsedCurrentTab),
    [isAssignedTab, assignedTasks, parsedEvents, parsedCurrentTab]
  );

  const listLoading = isAssignedTab ? assignedTasks === null : parsedLoading;

  const parsedInitialEvent = getInitialEventByType("task", customEventProps);

  return (
    <Stack gap={2}>
      <CalendarEventDialog
        open={open}
        event={parsedInitialEvent}
        onClose={closeModal}
        deleteEvent={deleteEvent}
        createOrUpdateEvent={createOrUpdateEvent}
      />
      <Stack direction="row" justifyContent="space-between">
        {listingEvents ? (
          <Typography variant="h5">{translate("listingsMatch.events")}</Typography>
        ) : (
          <Typography variant="h3">{translate("calendarPage.tasks.myDayTitle")}</Typography>
        )}
        <Button
          size="small"
          variant="contained"
          startIcon={<AddTask />}
          onClick={onAddNewTask ?? openModal}
        >
          {translate("calendarPage.control.newTask")}
        </Button>
      </Stack>
      <Container simple={useSimpleUi}>
        <Stack gap={2}>
          {!useSimpleUi && (
            <>
              <CalendarTasksTabs
                events={parsedEvents}
                loading={parsedLoading}
                assignedOpenCount={
                  showAssignedTab
                    ? assignedTasks === null
                      ? null
                      : assignedTasks.filter((task) => task.status !== "done").length
                    : undefined
                }
                currentTab={currentTab}
                onCurrentTabChange={handleCurrentTabChange}
              />
              <CalendarTasksToolbar
                currentTab={currentTab}
                onMoveAllOverdueTasksToToday={handleMoveAllOverdueTasksToToday}
              />
            </>
          )}
          <Stack
            sx={{
              maxHeight: 1120,
              overflowY: "auto"
            }}
          >
            {listLoading ? (
              <CalendarTasksLoading />
            ) : filteredEvents.length ? (
              filteredEvents.map((event) => (
                <CalendarTasksRow
                  event={event}
                  currentTab={currentTab}
                  inListingMatch={inListingMatch}
                  key={event.id}
                />
              ))
            ) : (
              <CalendarTasksNotFound
                currentTab={currentTab}
                inListingMatch={inListingMatch}
                overdueEventsCount={overdueEvents.length}
                onMoveAllOverdueTasksToToday={handleMoveAllOverdueTasksToToday}
              />
            )}
          </Stack>
        </Stack>
      </Container>
    </Stack>
  );
}

type ContainerProps = {
  simple?: boolean;
  children: ReactNode;
};

function Container({ simple, children }: ContainerProps): JSX.Element {
  if (simple) {
    return <>{children}</>;
  }

  return <Card sx={{ minHeight: 492 }}>{children}</Card>;
}
